import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import type { IMyLedProject, IProject, ProjectBudgetStatus } from '@pulse/shared';
import { Box, Chip, Divider, Paper, Stack, Tooltip, Typography } from '@pulse/ui';
import { type Rect, useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import { memo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatDdMmYyyy } from '../../../lib/date.util';
import { ProjectCompletionBar } from '../../delivery/components/ProjectCompletionMini';

/** One row of the card: the project record (name, dates, phase) paired with its figures from
 *  GET /me/led-projects (completion, budget). */
export interface LedProjectRow {
  project: IProject;
  figures: IMyLedProject;
}

function formatUsd(amountCents: number): string {
  return `$${(amountCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// Same label/colour table the Project Insights Budget column uses, so a project's budget reads
// identically on both screens rather than inventing a second vocabulary for the same status.
const BUDGET_CONFIG: Record<ProjectBudgetStatus, { label: string; color: 'success' | 'error' | 'default' }> = {
  onTrack: { label: 'On Track', color: 'success' },
  overBudget: { label: 'Over Budget', color: 'error' },
  unbudgeted: { label: 'Unbudgeted', color: 'default' },
};

// Mirrors ReportingService.computeBudgetStatus — done client-side here because the card reads
// GET /me/led-projects rather than the Project Insights report, which is what keeps it off
// `reporting:read`.
function budgetStatusOf(budgetedCents: number, actualCents: number): ProjectBudgetStatus {
  if (budgetedCents === 0) return 'unbudgeted';
  return actualCents > budgetedCents ? 'overBudget' : 'onTrack';
}

// The two phases live under different routes, so a pre-sales project sent to
// /delivery/projects/:id would 404.
function projectDetailPath(project: IProject): string {
  return project.phase === 'delivery' ? `/delivery/projects/${project.id}` : `/pre-sales/projects/${project.id}`;
}

// Spend vs budget for one project, rendered as the same chip the Project Insights Budget column
// uses — adherence % when there is a budget to measure against, the status label otherwise, with
// the $ figures on hover.
//
// The caller may hold no finance permission at all. The API decides that per project (the same
// `project_finance:read` rule as GET /projects/:id/finance/burn-rate) and simply leaves
// budgetedCents/actualCents off a project the caller can't see, so this falls back to "NA". There
// is no permission check to duplicate here.
function BudgetChip({ figures }: { figures: IMyLedProject }) {
  const hasFigures = figures.budgetedCents !== undefined && figures.actualCents !== undefined;
  const budgeted = figures.budgetedCents ?? 0;
  const actual = figures.actualCents ?? 0;
  const status = budgetStatusOf(budgeted, actual);

  // A chip is only worth its width when it carries a number. "Unbudgeted" and "unavailable" both
  // mean "no figure to show", so they render as muted text at this size rather than as a wide
  // grey pill that crowds the dates out of the row — Project Insights can afford the full label
  // in a dedicated table column, this rail cannot.
  if (!hasFigures || status === 'unbudgeted') {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        <MonetizationOnIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        <Typography variant="caption" color="text.disabled">
          {hasFigures ? 'No budget' : 'NA'}
        </Typography>
      </Stack>
    );
  }

  return (
    <Tooltip title={`Budgeted ${formatUsd(budgeted)} · Actual ${formatUsd(actual)}`}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
        <MonetizationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Chip
          label={`${Math.round((actual / budgeted) * 100)}%`}
          size="small"
          color={BUDGET_CONFIG[status].color}
          variant="filled"
        />
      </Stack>
    </Tooltip>
  );
}

// Memoized so a figures refresh (e.g. after a status change on Home) redraws only the rows whose
// project or figures actually changed — React Query keeps unchanged records as the same objects.
const ProjectRow = memo(function ProjectRow({ project, figures, onOpen }: LedProjectRow & { onOpen: (project: IProject) => void }) {
  return (
    <Stack
      spacing={0.75}
      onClick={() => onOpen(project)}
      sx={{ cursor: 'pointer', '&:hover .project-name': { textDecoration: 'underline' } }}
    >
      <Typography className="project-name" variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
        {project.projectKey} · {project.name}
      </Typography>

      {/* Dates and budget share one line — the dates wrap if they must, the chip never shrinks. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        {/* Same "Start: … / Target: …" labelling and em-dash placeholder the Project Insights
            Schedule column uses, kept on one line here to fit the rail. */}
        <Typography variant="caption" color="text.secondary">
          Start: {formatDdMmYyyy(project.startDate)} · Target:{' '}
          {project.targetDate ? formatDdMmYyyy(project.targetDate) : '—'}
        </Typography>
        <BudgetChip figures={figures} />
      </Stack>

      {/* The same completion widget the Delivery Projects list uses, so a project reads
          identically on both screens — fed the precomputed figures instead of fetching the
          project's issues. */}
      <ProjectCompletionBar project={project} percent={figures.completionPercent} issueCount={figures.issueCount} />
    </Stack>
  );
});

// ── Drawing only the visible rows ───────────────────────────────────────────
// The list box's own height follows the task panel beside it (see the Paper comment below), so
// which rows are on screen depends on two things at once: the box's own scroll position, and
// how much of the box the browser window shows. With a long task list the box is as tall as
// that list and scrolls with the page; with a short one it scrolls inside itself; below md it
// sits in normal flow and scrolls with the page. A stock virtualizer follows only one of those,
// so these three functions report "the part of the list that's actually visible" by combining
// both — the virtualizer itself is otherwise standard.

// How far the list box's top edge has been scrolled up past the top of the browser window.
function hiddenAboveWindow(el: Element): number {
  return Math.max(0, -el.getBoundingClientRect().top);
}

// Visible size: the part of the box inside the window. 0 when the box is off screen, which still
// leaves the overscan rows mounted around the current position.
function observeVisibleRect(instance: Virtualizer<HTMLDivElement, Element>, cb: (rect: Rect) => void) {
  const el = instance.scrollElement;
  if (!el) return;
  const update = () => {
    const r = el.getBoundingClientRect();
    cb({ width: el.clientWidth, height: Math.max(0, Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0)) });
  };
  update();
  const observer = new ResizeObserver(update);
  observer.observe(el);
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  return () => {
    observer.disconnect();
    window.removeEventListener('scroll', update);
    window.removeEventListener('resize', update);
  };
}

// Visible start: the box's own scroll position plus whatever the page has scrolled past its top.
function observeVisibleOffset(instance: Virtualizer<HTMLDivElement, Element>, cb: (offset: number, isScrolling: boolean) => void) {
  const el = instance.scrollElement;
  if (!el) return;
  const read = () => el.scrollTop + hiddenAboveWindow(el);
  let settle: number | undefined;
  const onScroll = () => {
    cb(read(), true);
    window.clearTimeout(settle);
    settle = window.setTimeout(() => cb(read(), false), 150);
  };
  cb(read(), false);
  el.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => {
    window.clearTimeout(settle);
    el.removeEventListener('scroll', onScroll);
    window.removeEventListener('scroll', onScroll);
  };
}

// The inverse, for when the virtualizer corrects the position after measuring a row: turn the
// combined offset back into the box's own scrollTop. When the box doesn't overflow (it's
// scrolling with the page) this is a no-op, exactly as scrolling a non-scrollable box is.
function scrollListTo(offset: number, { adjustments = 0, behavior }: { adjustments?: number; behavior?: ScrollBehavior }, instance: Virtualizer<HTMLDivElement, Element>) {
  const el = instance.scrollElement;
  if (!el) return;
  el.scrollTo({ top: offset + adjustments - hiddenAboveWindow(el), behavior });
}

// Rows are measured once mounted; this only needs to be close (≈ a row with a one-line name,
// including the divider and gaps between rows).
const ESTIMATED_ROW_HEIGHT = 109;
const OVERSCAN_ROWS = 5;

/** Home's Lead-view rail card: one row per project the caller leads or manages, showing the
 *  name, dates, spend-vs-budget and the same completion bar the Delivery Projects list shows.
 *
 *  Everything here comes from the ordinary projects list plus GET /me/led-projects rather than
 *  the Project Insights report, so the card needs only `project:read` — the permission that
 *  already lets a lead open their projects at all. Budget is the one part that can be withheld,
 *  and the API leaves it out where it is (see BudgetChip). No row makes a request of its own.
 *
 *  Only the rows near the visible part of the card are mounted (a lead can run hundreds of
 *  projects), and the card is memoized so the Home page re-rendering as its other queries land
 *  doesn't redraw it. */
export const LedProjectsCard = memo(function LedProjectsCard({ projects }: { projects: LedProjectRow[] }) {
  const navigate = useNavigate();
  const openProject = useCallback((p: IProject) => navigate(projectDetailPath(p)), [navigate]);
  const listRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: projects.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    getItemKey: (index) => projects[index]?.project.id ?? index,
    overscan: OVERSCAN_ROWS,
    observeElementRect: observeVisibleRect,
    observeElementOffset: observeVisibleOffset,
    scrollToFn: scrollListTo,
  });
  if (projects.length === 0) return null;

  // From md up the card is taken out of flow (its Grid column is position: relative), so its own
  // content can't grow the row — the work panel beside it is left as the only thing setting the
  // row height, and inset: 0 stretches the card to exactly that. A plain height: '100%' does not
  // work here: flex rows size to their tallest item, so a card with many projects would define
  // the height it was meant to be capped by and never scroll.
  // Below md the columns stack, there is nothing to match, and the card returns to normal flow.
  return (
    <Paper
      sx={{
        p: 2,
        pr: 0.5,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        position: { xs: 'static', md: 'absolute' },
        inset: { md: 0 },
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, flexShrink: 0 }}>
        My Projects
      </Typography>
      {/* minHeight: 0 is what lets this shrink below its content — a flex child otherwise floors
          at content height and the overflow never engages. */}
      <Box ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1.5 }}>
        <Box sx={{ position: 'relative' }} style={{ height: virtualizer.getTotalSize() }}>
          {virtualizer.getVirtualItems().map((item) => {
            const row = projects[item.index];
            if (!row) return null;
            // Same separation the list had as a Stack with a divider: 12px, a divider line, 12px.
            return (
              <Box
                key={item.key}
                data-index={item.index}
                ref={virtualizer.measureElement}
                sx={{ position: 'absolute', left: 0, right: 0, pt: item.index === 0 ? 0 : 1.5 }}
                style={{ top: item.start }}
              >
                {item.index > 0 && <Divider sx={{ mb: 1.5 }} />}
                <ProjectRow project={row.project} figures={row.figures} onOpen={openProject} />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
});
